export type DeliveryAddress = {
  addressLine1: string;
  addressLine2: string;
  suburb: string;
  postcode: string;
  state: string;
};

type AddressComponentLike = {
  longText?: string;
  shortText?: string;
  long_name?: string;
  short_name?: string;
  types?: string[];
};

const emptyDeliveryAddress = (): DeliveryAddress => ({
  addressLine1: "",
  addressLine2: "",
  suburb: "",
  postcode: "",
  state: "",
});

const componentText = (
  component: AddressComponentLike | undefined,
  preferShort = false
): string => {
  if (!component) {
    return "";
  }
  if (preferShort) {
    return (
      component.shortText ||
      component.short_name ||
      component.longText ||
      component.long_name ||
      ""
    );
  }
  return (
    component.longText ||
    component.long_name ||
    component.shortText ||
    component.short_name ||
    ""
  );
};

const findComponent = (
  components: AddressComponentLike[],
  type: string
): AddressComponentLike | undefined =>
  components.find((component) => component.types?.includes(type));

/**
 * Build a structured AU delivery address from Google Places address components.
 * - addressLine1: unit (if any) + street number + route
 * - addressLine2: reserved for additional address details
 * - suburb: locality / postal town
 * - postcode / state from postal_code and administrative_area_level_1
 */
export const deliveryAddressFromPlaceComponents = (
  components: AddressComponentLike[] | null | undefined,
  fallbackFormattedAddress?: string | null
): DeliveryAddress | null => {
  const list = Array.isArray(components) ? components : [];

  const subpremise = componentText(findComponent(list, "subpremise"));
  const streetNumber = componentText(findComponent(list, "street_number"));
  const route = componentText(findComponent(list, "route"));
  const locality =
    componentText(findComponent(list, "locality")) ||
    componentText(findComponent(list, "postal_town")) ||
    componentText(findComponent(list, "sublocality")) ||
    componentText(findComponent(list, "sublocality_level_1"));
  const postcode = componentText(findComponent(list, "postal_code"));
  const state = componentText(
    findComponent(list, "administrative_area_level_1"),
    true
  );

  let addressLine1 = [streetNumber, route].filter(Boolean).join(" ").trim();
  if (subpremise) {
    addressLine1 = addressLine1
      ? `${subpremise}/${addressLine1}`
      : subpremise;
  }

  // Fallback when Places returns a formatted string but sparse components.
  if (!addressLine1 && fallbackFormattedAddress) {
    const parts = fallbackFormattedAddress.split(",").map((part) => part.trim());
    addressLine1 = parts[0] || fallbackFormattedAddress;
    if (!locality && parts[1]) {
      return {
        addressLine1,
        addressLine2: "",
        suburb: parts[1],
        postcode,
        state,
      };
    }
  }

  if (!addressLine1) {
    return null;
  }

  return {
    addressLine1,
    addressLine2: "",
    suburb: locality,
    postcode,
    state,
  };
};

export const isDeliveryAddressSet = (
  address: DeliveryAddress | string | null | undefined
): address is DeliveryAddress => {
  if (!address || typeof address === "string") {
    return false;
  }
  return Boolean(address.addressLine1?.trim());
};

export const formatDeliveryAddress = (
  address: DeliveryAddress | string | null | undefined
): string => {
  if (!address) {
    return "";
  }
  if (typeof address === "string") {
    return address;
  }
  const line1 = address.addressLine1?.trim() || "";
  const line2 = address.addressLine2?.trim() || "";
  const suburb = address.suburb?.trim() || "";
  const statePostcode = [address.state?.trim(), address.postcode?.trim()]
    .filter(Boolean)
    .join(" ");
  return [line1, line2, suburb, statePostcode].filter(Boolean).join(", ");
};

export const createEmptyDeliveryAddress = emptyDeliveryAddress;
